import React, { useMemo } from "react";

import { IconButton, useMediaQuery } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router";

import CartModal from "components/CartModal";
import RouteDrawer, { RouteDrawerProps } from "components/RouteDrawer";
import makeStyles from "hooks/makeStyles";
import useLocale from "hooks/useLocale";

const useStyles = makeStyles(theme => ({
	root: {},
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start",
		[theme.breakpoints.up(480)]: {
			display: "none"
		}
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff"
	},
	paper: {
		borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
		backgroundColor: "rgba(33,33,33,0.7)",
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(2),
		display: "flex",
		height: "calc(var(--vh, 1vh) * 100 - 45px)",
		[theme.breakpoints.up(480)]: {
			height: "100vh",
			borderRadius: "0",
			width: "80%",
			maxWidth: "442px",
			left: "auto",
			backgroundColor: theme.palette.background.paper,
			padding: 0,
			color: theme.palette.text.primary
		},
		[theme.breakpoints.up(769)]: {
			width: "50%"
		}
	},
	backdrop: {
		backgroundColor: "rgba(0,0,0,0.1)",
		[theme.breakpoints.up(480)]: {
			backgroundColor: "transparent",
			WebkitBackdropFilter: "blur(1.8px)",
			backdropFilter: "blur(1.8px)"
		}
	}
}));

type Props = Omit<RouteDrawerProps, "swipeable" | "matchRoute"> & {};

const CartDrawer = ({ className, ...otherProps }: Props) => {
	const { locale } = useLocale();
	const { hash } = useLocation();
	const navigate = useNavigate();
	const classes = useStyles();
	const mobile = useMediaQuery("(max-width:480px)");

	const onClose = useMemo(
		() => () => {
			if (hash === "#cart") {
				navigate("#");
			}
		},
		[hash, navigate]
	);

	const handleChange = useMemo(
		() => (value: string) => {
			navigate(`/${locale}/${value}`);
		},
		[locale, navigate]
	);

	const props: RouteDrawerProps = useMemo(
		() =>
			Object.assign(
				{
					className: clsx(classes.root, className),
					classes: { paper: classes.paper },
					BackdropProps: {
						className: classes.backdrop
					},
					anchor: mobile ? "bottom" : "right",
					disableSwipeToOpen: true,
					swipeable: true,
					matchRoute: ({ location: { hash } }) => hash === "#cart",
					onClose,
					onOpen: () => {}
				},
				otherProps
			),
		[className, classes.backdrop, classes.paper, classes.root, mobile, onClose, otherProps]
	);

	return (
		<RouteDrawer {...props}>
			<IconButton className={classes.closeButton} onClick={onClose}>
				<Close className={classes.closeIcon} />
			</IconButton>
			<CartModal onChange={handleChange} />
		</RouteDrawer>
	);
};

export default CartDrawer;
